import React from "react"
import { graphql } from "gatsby"
import { useTranslation } from "gatsby-plugin-react-i18next"
import { withPrismicPreview } from "gatsby-plugin-prismic-previews"

import { Element, Text } from "legacy/components/shared/base"
import RichText from "legacy/components/shared/RichText/RichText"
import { Layout } from "legacy/components/templates"
import { LegalPageQuery } from "types/__generated__/gatsby-graphql"
import SEO from "legacy/components/shared/SEO"
import { linkResolver } from "src/legacy/utils"
import { defaultCanonicalLink, useTheme } from "src/legacy/utils"

type LegalPageProps = {
  data: LegalPageQuery
}

export const pageQuery = graphql`
  query LegalPage($uid: String!, $language: String! = "en") {
    locales: allLocale(
      filter: { ns: { in: ["shared"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    prismicLegalPage(uid: { eq: $uid }) {
      _previewable
      data {
        canonical_url
        content {
          html
          richText
        }
        effective_date
        title {
          text
        }
        og_description
        og_image {
          url
        }
        og_title
      }
      uid
      url
    }
    site {
      siteMetadata {
        siteURL
      }
    }
  }
`

export const Head = pageData => {
  const prismicLegalPage = pageData?.data?.prismicLegalPage
  const { canonical_url, og_description, og_image, og_title } =
    prismicLegalPage?.data
  const title = prismicLegalPage?.data?.title?.text || "legal"
  const canonicalUrl =
    canonical_url ||
    defaultCanonicalLink(
      prismicLegalPage?.url,
      pageData?.data?.site?.siteMetaData?.siteUrl
    )

  return (
    <SEO
      canonicalUrl={canonicalUrl}
      ogDescription={og_description}
      ogImage={og_image?.url}
      ogTitle={og_title}
      ogType="page"
      title={title}
    />
  )
}

const LegalPage: React.FC<LegalPageProps> = ({
  data: { prismicLegalPage },
}): React.ReactElement | null => {
  if (!prismicLegalPage?.data) return null
  const { t } = useTranslation("shared", {
    keyPrefix: "legal_pages",
  })

  const pageData = prismicLegalPage?.data
  const { sizes } = useTheme()

  return (
    <Layout>
      <Element m="0 auto" maxWidth={sizes.contentWidth} width="100%">
        <Element p={["50px 40px 0", "80px 40px 0"]}>
          <Text
            as="h4"
            fontSize={["36", "text.headerLarge"]}
            lineHeight={["44px", "normal"]}
            mb={["50", "100"]}
            textAlign={["left", "left", "center"]}
            variant="headerLarge"
          >
            {pageData.title?.text}
          </Text>
          {pageData.effective_date ? (
            <Text
              as="h2"
              fontSize={["14", "18"]}
              mb={["10", "20"]}
              variant="subheaderSmall"
            >
              {t("effective_date")}: {pageData.effective_date}
            </Text>
          ) : null}
          <RichText mb={["50", "100"]} render={pageData.content?.richText} />
        </Element>
      </Element>
    </Layout>
  )
}

export default withPrismicPreview(LegalPage, [
  {
    repositoryName: process.env.GATSBY_PRISMIC_REPO_NAME,
    linkResolver,
  },
])
